import { Alert, Button, Stack } from "@mui/material";
import * as React from "react";
import { useLanguage } from "./Language";
import * as dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Patron } from "@tlc/ls2pac/dto/Patron";
import { AccountSummary } from "@tlc/ls2pac/dto/AccountSummary";

export interface AccountSummaryAlertsProps {
  patron: Patron;
  accountSummary: AccountSummary;
}

export const AccountSummaryAlerts: React.FC<AccountSummaryAlertsProps> = (
  props
) => {
  const { patron, accountSummary } = props;
  const isExpired = dayjs().isAfter(dayjs(patron.cardExpirationDate), "day");
  const { translate } = useLanguage();

  const convertPenniesToCurrency = (value: number, locale = "en-US") => {
    const pennies = !value || isNaN(value) ? 0 : value;
    const dollars = pennies / 100;
    const formattedCurrency = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(dollars);

    return formattedCurrency;
  };

  return (
    <Stack sx={{ width: "100%" }} spacing={2}>
      {isExpired && <Alert severity="error">{translate("expiredCard")}</Alert>}
      {accountSummary.itemsOverdue > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              component={Link}
              to="/myaccount/loans"
              color="inherit"
              size="small"
            >
              {translate("viewLoans")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.itemsOverdue}</strong>{" "}
          {translate("itemsOverdue")}
        </Alert>
      )}

      {accountSummary.fees > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              component={Link}
              to="/myaccount/fines"
              color="inherit"
              size="small"
            >
              {translate("viewFines")}
            </Button>
          }
        >
          {translate("youOwe")}{" "}
          <strong>{convertPenniesToCurrency(patron.fees)}</strong>{" "}
          {translate("fees")}
        </Alert>
      )}

      {accountSummary.arrivedHolds > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              component={Link}
              to="/myaccount/holds"
              color="inherit"
              size="small"
            >
              {translate("viewHolds")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.arrivedHolds}</strong>{" "}
          {translate("arrivedHolds")}
        </Alert>
      )}

      {accountSummary.loanCount > 0 && (
        <Alert
          severity="info"
          action={
            <Button
              component={Link}
              to="/myaccount/loans"
              color="inherit"
              size="small"
            >
              {translate("viewLoans")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.loanCount}</strong>{" "}
          {translate("loanCount")}
        </Alert>
      )}

      {accountSummary.pendingHolds > 0 && (
        <Alert
          severity="info"
          action={
            <Button
              component={Link}
              to="/myaccount/holds"
              color="inherit"
              size="small"
            >
              {translate("viewHolds")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.pendingHolds}</strong>{" "}
          {translate("pendingHolds")}
        </Alert>
      )}
    </Stack>
  );
};
